/**
 * Tiny WebAudio synthesiser — no audio assets to download (fast first load, works offline).
 * Every sound respects the user's volume/sound settings.
 */
import { useSettings } from '../store/settings';

let ctx: AudioContext | null = null;
let musicNodes: { stop(): void } | null = null;

function ac(): AudioContext | null {
  try {
    ctx ??= new AudioContext();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

function volume(kind: 'fx' | 'music'): number {
  const s = useSettings.getState().settings;
  if (kind === 'fx' && !s.sound) return 0;
  if (kind === 'music' && !s.music) return 0;
  return s.masterVolume * (kind === 'fx' ? s.effectsVolume : s.musicVolume);
}

function tone(freq: number, dur: number, type: OscillatorType = 'sine', gain = 0.2, delay = 0): void {
  const a = ac();
  const v = volume('fx');
  if (!a || v <= 0) return;
  const t = a.currentTime + delay;
  const o = a.createOscillator();
  const g = a.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(gain * v, t + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g).connect(a.destination);
  o.start(t);
  o.stop(t + dur + 0.02);
}

function noise(dur: number, gain = 0.15, hp = 2000): void {
  const a = ac();
  const v = volume('fx');
  if (!a || v <= 0) return;
  const len = Math.floor(a.sampleRate * dur);
  const buf = a.createBuffer(1, len, a.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len) ** 2;
  const src = a.createBufferSource();
  src.buffer = buf;
  const f = a.createBiquadFilter();
  f.type = 'highpass';
  f.frequency.value = hp;
  const g = a.createGain();
  g.gain.value = gain * v;
  src.connect(f).connect(g).connect(a.destination);
  src.start();
}

export const sfx = {
  tap: () => tone(660, 0.05, 'triangle', 0.08),
  card: () => noise(0.08, 0.25, 2500),
  deal: () => {
    for (let i = 0; i < 4; i++) setTimeout(() => noise(0.05, 0.18, 3000), i * 70);
  },
  chip: () => {
    tone(2100, 0.06, 'square', 0.04);
    tone(2600, 0.05, 'square', 0.03, 0.04);
  },
  turn: () => {
    tone(880, 0.12, 'sine', 0.15);
    tone(1320, 0.16, 'sine', 0.12, 0.12);
  },
  win: () => [523, 659, 784, 1047].forEach((f, i) => tone(f, 0.22, 'triangle', 0.16, i * 0.11)),
  lose: () => [392, 330, 262].forEach((f, i) => tone(f, 0.25, 'sine', 0.12, i * 0.14)),
  coin: () => [1200, 1600].forEach((f, i) => tone(f, 0.09, 'triangle', 0.12, i * 0.07)),
  error: () => tone(180, 0.18, 'sawtooth', 0.06),
  notify: () => tone(1000, 0.1, 'sine', 0.1),
};

/** Gentle generative pad for the lobby (off by default when music is disabled). */
export function startMusic(): void {
  stopMusic();
  const a = ac();
  const v = volume('music');
  if (!a || v <= 0) return;
  const master = a.createGain();
  master.gain.value = 0.05 * v;
  master.connect(a.destination);
  const chords = [
    [220, 277.2, 329.6],
    [196, 246.9, 293.7],
    [174.6, 220, 261.6],
    [196, 246.9, 329.6],
  ];
  const oscs: OscillatorNode[] = [];
  chords[0]!.forEach((f) => {
    const o = a.createOscillator();
    o.type = 'sine';
    o.frequency.value = f;
    o.connect(master);
    o.start();
    oscs.push(o);
  });
  let i = 0;
  const timer = setInterval(() => {
    i = (i + 1) % chords.length;
    oscs.forEach((o, k) => o.frequency.setTargetAtTime(chords[i]![k]!, a.currentTime, 0.8));
  }, 4000);
  musicNodes = {
    stop() {
      clearInterval(timer);
      oscs.forEach((o) => o.stop());
      master.disconnect();
    },
  };
}

export function stopMusic(): void {
  musicNodes?.stop();
  musicNodes = null;
}
